<template>
    <div v-if="isEditor" class="container nutrition_status_page">
        <h1>{{ $t('nutrition_status.title') }}</h1>
        <p class="error_text" v-if="error">{{ error }}</p>

        <div class="stat_grid" v-if="site_stats">
            <div class="stat_tile">
                <span class="stat_value">{{ site_stats.total_recipes }}</span>
                <span class="stat_label">{{ $t('nutrition_status.total_recipes') }}</span>
            </div>
            <div class="stat_tile">
                <span class="stat_value">{{ site_stats.total_users }}</span>
                <span class="stat_label">{{ $t('nutrition_status.total_users') }}</span>
            </div>
            <div class="stat_tile">
                <span class="stat_value">{{ site_stats.total_reactions }}</span>
                <span class="stat_label">{{ $t('nutrition_status.total_reactions') }}</span>
            </div>
            <div class="stat_tile">
                <span class="stat_value">{{ site_stats.total_ratings }}</span>
                <span class="stat_label">{{ $t('nutrition_status.total_ratings') }}</span>
            </div>
        </div>

        <div class="tab_bar" v-if="tabs.length > 0">
            <span
                class="tab"
                :class="activeTab === t.key ? 'active_tab' : 'inactive_tab'"
                v-for="t in tabs"
                :key="t.key"
                @click="activeTab = t.key"
            >
                {{ t.label }}
                <span class="tab_count">{{ t.count }}</span>
            </span>
        </div>

        <div class="ai_list_section" v-if="activeTab === 'missing'">
            <p class="subtext">{{ $t('nutrition_status.missing_hint') }}</p>
            <ul class="ai_list">
                <li v-for="name in missing_nutrition_names" :key="name">
                    <router-link :to="{ name: 'IngredientInternal', params: { name: name } }">{{ name }}</router-link>
                </li>
            </ul>
        </div>

        <div class="ai_list_section" v-if="activeTab === 'recent'">
            <ul class="ai_list">
                <li v-for="name in recent_user_submitted" :key="name">
                    <router-link :to="{ name: 'IngredientInternal', params: { name: name } }">{{ name }}</router-link>
                </li>
            </ul>
        </div>

        <div class="ai_list_section" v-if="activeTab === 'unresolvable'">
            <p class="subtext">{{ $t('nutrition_status.unresolvable_hint') }}</p>

            <div class="stat_grid" v-if="visibleReasonCounts.length > 0">
                <div
                    class="stat_tile reason_tile"
                    :class="'reason_tile--' + entry.reason"
                    v-for="entry in visibleReasonCounts"
                    :key="entry.reason"
                >
                    <span class="stat_value">{{ entry.count }}</span>
                    <span class="stat_label">{{ $t('nutrition_status.reason_' + entry.reason) }}</span>
                </div>
            </div>

            <div class="unresolvable_toolbar">
                <span class="unresolvable_count">{{ $t('nutrition_status.unresolvable_count', { count: recipes_with_unresolvable_nutrition.length }) }}</span>
                <button class="link_btn" @click="toggleAllExpanded">
                    {{ allExpanded ? $t('nutrition_status.collapse_all') : $t('nutrition_status.expand_all') }}
                </button>
            </div>

            <div class="unresolvable_list">
                <div class="unresolvable_row" v-for="recipe in recipes_with_unresolvable_nutrition" :key="recipe.id">
                    <div class="unresolvable_row_header" @click="toggleExpanded(recipe.id)">
                        <span class="chevron" :class="{ chevron__open: isExpanded(recipe.id) }">▸</span>
                        <router-link
                            class="recipe_link"
                            :to="{ name: 'RecipeInternal', params: { id: recipe.id } }"
                            @click.native.stop
                        >{{ recipe.name }}</router-link>
                        <span class="issue_count_badge">{{ recipe.ingredients.length }}</span>
                        <span
                            class="reason_dot"
                            :class="'reason_dot--' + reason"
                            v-for="reason in recipe.reasons"
                            :key="reason"
                            :title="$t('nutrition_status.reason_' + reason)"
                        ></span>
                    </div>
                    <ul class="reason_list" v-show="isExpanded(recipe.id)">
                        <li v-for="(item, index) in recipe.ingredients" :key="item.name + item.reason + index">
                            <router-link
                                v-if="item.linkable"
                                :to="{ name: 'IngredientInternal', params: { name: item.name } }"
                            >{{ item.name }}</router-link>
                            <span v-else>{{ item.name }}</span>
                            <span class="reason_badge" :class="'reason_badge--' + item.reason">{{ $t('nutrition_status.reason_' + item.reason) }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        <h1>{{ $t('nutrition_status.no_access') }}</h1>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "NutritionStatus",
    data() {
        return {
            site_stats: null,
            missing_nutrition_names: [],
            recent_user_submitted: [],
            recipes_with_unresolvable_nutrition: [],
            unresolvable_reason_counts: null,
            reasonOrder: ["no-nutrition-data", "unconvertible-unit", "unknown-ingredient", "unparseable-quantity"],
            expandedRecipes: {},
            activeTab: null,
            error: null
        }
    },
    computed: {
        ...mapGetters({
            user: "user"
        }),
        isEditor() {
            return !!(this.user.data && this.user.data.profile && (this.user.data.profile.admin || this.user.data.profile.editor));
        },
        visibleReasonCounts() {
            if (!this.unresolvable_reason_counts) { return []; }
            return this.reasonOrder
                .filter((reason) => this.unresolvable_reason_counts[reason])
                .map((reason) => ({ reason: reason, count: this.unresolvable_reason_counts[reason] }));
        },
        allExpanded() {
            return this.recipes_with_unresolvable_nutrition.length > 0 &&
                this.recipes_with_unresolvable_nutrition.every((recipe) => this.expandedRecipes[recipe.id]);
        },
        // Only lists with something in them get a tab — an empty tab is dead
        // weight an editor has to click through for nothing.
        tabs() {
            let all = [
                { key: "missing", label: this.$t("nutrition_status.missing_title"), count: this.missing_nutrition_names.length },
                { key: "unresolvable", label: this.$t("nutrition_status.unresolvable_title"), count: this.recipes_with_unresolvable_nutrition.length },
                { key: "recent", label: this.$t("nutrition_status.recent_user_submitted_title"), count: this.recent_user_submitted.length }
            ];
            return all.filter((t) => t.count > 0);
        }
    },
    mounted() {
        if (this.isEditor) {
            this.getStatus();
        }
    },
    watch: {
        isEditor(value) {
            if (value) {
                this.getStatus();
            }
        }
    },
    methods: {
        getStatus() {
            fetch(process.env.VUE_APP_API + `/ingredients/nutrition_status`, {
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token)
                }
            }).then(res => res.json())
              .then(res => {
                  if (res.error) {
                      this.error = typeof res.error === "string" ? res.error : JSON.stringify(res.error);
                  } else {
                      this.site_stats = res.site_stats || null;
                      this.missing_nutrition_names = res.missing_nutrition_names || [];
                      this.recent_user_submitted = res.recent_user_submitted || [];
                      this.recipes_with_unresolvable_nutrition = res.recipes_with_unresolvable_nutrition || [];
                      this.unresolvable_reason_counts = res.unresolvable_reason_counts || null;
                      this.expandedRecipes = {};
                      // Keep the current tab if it's still populated (e.g. after a
                      // re-fetch); otherwise fall back to the first non-empty one.
                      if (!this.tabs.some((t) => t.key === this.activeTab)) {
                          this.activeTab = this.tabs.length > 0 ? this.tabs[0].key : null;
                      }
                  }
              });
        },
        isExpanded(recipeId) {
            return !!this.expandedRecipes[recipeId];
        },
        toggleExpanded(recipeId) {
            this.$set(this.expandedRecipes, recipeId, !this.expandedRecipes[recipeId]);
        },
        toggleAllExpanded() {
            let next = !this.allExpanded;
            let expanded = {};
            if (next) {
                for (let recipe of this.recipes_with_unresolvable_nutrition) {
                    expanded[recipe.id] = true;
                }
            }
            this.expandedRecipes = expanded;
        }
    }
}
</script>

<style scoped>
.nutrition_status_page {
    text-align: left;
    padding-top: 2rem;
    max-width: 840px;
}
.stat_grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin: 1.5rem 0;
}
.stat_tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 110px;
    padding: 1rem;
    border: var(--line);
    border-radius: 10px;
}
.stat_value {
    font-size: 1.6rem;
    font-weight: 700;
}
.stat_label {
    font-size: 0.8rem;
    color: var(--color-grey-2);
    text-align: center;
}
.tab_bar {
    display: flex;
    margin-top: 2rem;
}
.tab_count {
    display: inline-block;
    margin-left: 0.4rem;
    font-size: 0.75rem;
    font-weight: 700;
    background-color: var(--color-grey-3);
    color: var(--color-text-primary);
    border-radius: 3rem;
    padding: 0.05rem 0.5rem;
}
.active_tab .tab_count {
    background-color: rgba(255, 255, 255, 0.3);
    color: white;
}
.ai_list_section {
    margin-top: 0;
    padding-top: 1.5rem;
}
.ai_list_section .subtext {
    color: var(--color-grey-2);
    font-size: 0.85rem;
}
.ai_list {
    list-style: none;
    padding: 0;
    columns: 2;
}
.ai_list li {
    padding: 0.2rem 0;
}
.reason_tile {
    border-width: 1px;
    border-style: solid;
}
.reason_tile--no-nutrition-data { border-color: var(--color-orange); }
.reason_tile--unconvertible-unit { border-color: var(--color-secondary); }
.reason_tile--unknown-ingredient { border-color: var(--color-red); }
.reason_tile--unparseable-quantity { border-color: var(--color-grey-2); }

.unresolvable_toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 1rem 0 0.5rem;
}
.unresolvable_count {
    color: var(--color-grey-2);
    font-size: 0.85rem;
}
.link_btn {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-secondary);
    font-weight: 600;
    cursor: pointer;
    font-size: 0.85rem;
}
.link_btn:hover {
    text-decoration: underline;
}

.unresolvable_list {
    border-top: var(--line);
}
.unresolvable_row {
    border-bottom: var(--line);
}
.unresolvable_row_header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 0;
    cursor: pointer;
}
.chevron {
    display: inline-block;
    transition: transform 0.15s ease;
    color: var(--color-grey-2);
    font-size: 0.75rem;
}
.chevron__open {
    transform: rotate(90deg);
}
.recipe_link {
    flex: 1;
    font-weight: 600;
}
.issue_count_badge {
    background-color: var(--color-grey-3);
    color: var(--color-text-primary);
    border-radius: 3rem;
    padding: 0.05rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
}
.reason_dot {
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    display: inline-block;
}
.reason_dot--no-nutrition-data { background-color: var(--color-orange); }
.reason_dot--unconvertible-unit { background-color: var(--color-secondary); }
.reason_dot--unknown-ingredient { background-color: var(--color-red); }
.reason_dot--unparseable-quantity { background-color: var(--color-grey-2); }

.reason_list {
    list-style: none;
    padding: 0 0 0.75rem 1.3rem;
    margin: 0;
    font-size: 0.9rem;
}
.reason_list li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.15rem 0;
}
.reason_badge {
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 3rem;
    padding: 0.05rem 0.55rem;
    border: 1px solid;
    color: var(--color-grey-2);
}
.reason_badge--no-nutrition-data { border-color: var(--color-orange); color: var(--color-orange); }
.reason_badge--unconvertible-unit { border-color: var(--color-secondary); color: var(--color-secondary); }
.reason_badge--unknown-ingredient { border-color: var(--color-red); color: var(--color-red); }
.reason_badge--unparseable-quantity { border-color: var(--color-grey-2); color: var(--color-grey-2); }
.error_text, .empty_text {
    color: var(--color-grey-2);
}
</style>
